import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { Loading } from '@/shared/ui';
import { PageTransition } from '@/shared/ui/animations';
import { ListPageBody, ListPageLayout } from '@/shared/ui/layout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { useModalState } from '@/shared/hooks';
import { durations } from '@/shared/constants/motion.constants';
import { ROUTES } from '@/shared/config/routes';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { getCurrentOrganizationId } from '@/entities/user';
import { getPersonaFetchErrorMessage, usePersonaPolling } from '@/entities/persona';
import { personaLabel } from '@/shared/lib/persona-label';
import { SPECIAL_AGENT_IDS } from '@/entities/agent';
import { useUpdatePersona, useDeletePersona } from '@/features/persona-sparks';
import { EditPersonaForm } from '@/features/audiences';
import { useChatStore, startChatSession } from '@/features/chats';
import { AudienceDetailBody, AudienceHeaderV2, PersonaExportButton } from './components';

/**
 * How long the page fades for before "start conversation" navigates away, on
 * the `--duration-base` rung the fade class below is bound to. The wait reads
 * the token rather than restating it, so the chat cannot cut in over a profile
 * still on screen.
 */
const EXIT_SECONDS = durations.base;

const AudienceDetailInternal = () => {
  const { t } = useTranslation('audiences');

  const navigate = useNavigate();
  const { audienceId } = useParams<{ audienceId: string }>();
  const workspaceId = useCurrentWorkspaceId();
  const {
    data: persona,
    isPending,
    isError,
    error,
    isNotFound,
    refetch,
  } = usePersonaPolling(audienceId, workspaceId);
  const [isExiting, setIsExiting] = useState(false);
  const editFormModal = useModalState();
  // The persona-update mutation is owned at the page boundary and threaded into
  // `<EditPersonaForm />`, so the audiences slice stays cross-feature-OUT clean.
  const updatePersonaMutation = useUpdatePersona();
  const deletePersonaMutation = useDeletePersona();

  const handleDeleteAndRetry = useCallback(async () => {
    if (!audienceId) return;
    await deletePersonaMutation.mutateAsync({ id: audienceId });
    void navigate(ROUTES.KNOWLEDGE_HUB_AUDIENCES);
  }, [audienceId, deletePersonaMutation, navigate]);

  const handleStartConversation = useCallback(async () => {
    if (!persona) return;

    setIsExiting(true);
    await new Promise((resolve) => setTimeout(resolve, EXIT_SECONDS * 1000));

    // Set the agent in the store before initializing chat
    await useChatStore.getState().setAgentById(SPECIAL_AGENT_IDS.AUDIENCE_FLOW);

    const personaToTalkTo = personaLabel(persona);

    await startChatSession(useChatStore.getState().initializeChat, navigate, {
      agentId: SPECIAL_AGENT_IDS.AUDIENCE_FLOW,
      prompt: t('audienceDetail.iWantToSpeakWith', { persona: personaToTalkTo }),
      inputs: { personaId: persona.id },
      replace: true,
      visibilityScope: {
        workspaceId,
        organizationId: getCurrentOrganizationId(),
      },
    });
  }, [persona, workspaceId, navigate, t]);

  // One title for every state, so the breadcrumb and the heading never disagree
  // about what the page is called. A loaded audience is named as the audiences list names
  // its row, so the crumb ends on what the reader clicked to get here.
  const title = isError
    ? t('audienceDetail.audience')
    : isNotFound
      ? t('audienceDetail.audienceNotFound')
      : isPending
        ? t('audienceDetail.loading')
        : persona?.personaTitle || persona?.segmentName || t('audienceDetail.unnamedAudience');

  return (
    <>
      <ListPageLayout
        className={`transition-opacity duration-base ${isExiting ? 'opacity-0' : 'opacity-100'}`}
      >
        <PageTransition variant="slideUp" delay={0.0}>
          <AudienceHeaderV2
            title={title}
            persona={persona}
            action={<PersonaExportButton persona={persona} workspaceId={workspaceId} />}
            onEdit={editFormModal.open}
            onStartConversation={() => void handleStartConversation()}
          />
        </PageTransition>

        <ListPageBody>
          <PageTransition variant="slideUp" delay={0.1} className="flex min-h-0 flex-1 flex-col">
            {isError ? (
              <EmptyState
                className="flex-1"
                role="alert"
                title={t('audienceDetail.couldnTLoadAudience')}
                description={getPersonaFetchErrorMessage(error)}
                data-testid="audience-detail-error-state"
              >
                <Button variant="outline" onClick={() => void refetch()}>
                  {t('audienceDetail.tryAgain')}
                </Button>
              </EmptyState>
            ) : isNotFound ? (
              <EmptyState
                className="flex-1"
                title={t('audienceDetail.audienceNotFound')}
                data-testid="audience-detail-not-found-state"
              />
            ) : (
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-section">
                <AudienceDetailBody
                  persona={persona}
                  isPending={isPending}
                  onDeleteAndRetry={() => void handleDeleteAndRetry()}
                  isDeleting={deletePersonaMutation.isPending}
                />
              </div>
            )}
          </PageTransition>
        </ListPageBody>
      </ListPageLayout>

      {editFormModal.isMounted && persona && (
        <EditPersonaForm
          open={editFormModal.isOpen}
          persona={persona}
          updatePersona={updatePersonaMutation.mutateAsync}
          onClose={editFormModal.close}
          onExited={editFormModal.onExited}
          onSuccess={() => void refetch()}
        />
      )}
    </>
  );
};

export const AudienceDetail = () => {
  const workspaceId = useCurrentWorkspaceId();

  if (!workspaceId) {
    return <Loading />;
  }

  return <AudienceDetailInternal />;
};

AudienceDetail.displayName = 'AudienceDetail';
